/**
 * AdvancedScoringModal (v3 — action-first when player pre-selected)
 *
 * Normal flow (no preSelectedPlayer — legacy / block-assist):
 *   Step 1 – Player selector  → tap player card
 *   Step 2 – Action selector  → tap metric chip  → confirm + close
 *   Step 3 – (Block Assist only) Main blocker picker
 *
 * Inline-player flow (preSelectedPlayer provided):
 *   Modal opens directly at Step 2 — action selector only.
 *   No −1 in popup; only Cancel.
 *
 * Props:
 *   isOpen             boolean
 *   onClose            () => void
 *   onConfirm          (player, metricKey, metric, extraPlayers?) => void
 *   onDecrease         () => void   — quick –1 deduct (step 1 footer only)
 *   players            Array<PlayerObject>
 *   preSelectedPlayer  PlayerObject | null  — when set skip step 1
 *   teamLabel          string
 *   scorer             BaseSportScoring | null
 *   participant        'participant1' | 'participant2'
 *   sportKey           string
 */
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { SHARED_BLOCK_METRIC, getMetricTypes, withMetricType } from '../../../../config/sportsMetrics';

// ── Helpers ──────────────────────────────────────────────────────────────────

const playerDisplayName = (p) =>
  p?.name || p?.full_name || p?.username || 'Unknown Player';

const playerInitials = (p) => {
  const name = playerDisplayName(p);
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
};

const isBlockAssist = (metricKey) => metricKey === 'blockAssist';

// ── Sub-components ────────────────────────────────────────────────────────────

export const ActionChip = ({ metricKey, metric, restricted, selected, onSelect }) => (
  <button
    type="button"
    className={[
      'adv-metric-chip',
      metric.affectsScore        ? 'adv-metric-chip--score'      : '',
      metric.awardsOpponentPoint ? 'adv-metric-chip--opp'        : '',
      !metric.isPositive         ? 'adv-metric-chip--negative'   : '',
      restricted                 ? 'adv-metric-chip--restricted' : '',
      selected                   ? 'adv-metric-chip--selected'   : '',
    ].filter(Boolean).join(' ')}
    onClick={() => !restricted && onSelect(metricKey, metric)}
    disabled={restricted}
    title={restricted ? 'Not available for this position' : metric.description}
    aria-pressed={selected}
  >
    <span className="adv-metric-chip__label">{metric.label}</span>
    {metric.affectsScore        && <span className="adv-metric-chip__badge">+{metric.scorePoints || 1}</span>}
    {metric.awardsOpponentPoint && <span className="adv-metric-chip__badge adv-metric-chip__badge--opp">Opp+1</span>}
  </button>
);

/**
 * A chip plus its optional one-tap types (Kill → Tip / Placement / Setter Dump).
 * A type records the same metric with `typeKey`, so it counts once as that metric.
 */
export const ActionChipGroup = ({ metricKey, metric, player, selected = false, onSelect }) => {
  const types = getMetricTypes(metric, player);
  const chip = (
    <ActionChip metricKey={metricKey} metric={metric} restricted={false} selected={selected} onSelect={onSelect} />
  );
  if (types.length === 0) return chip;
  return (
    <div className="adv-metric-group" role="group" aria-label={`${metric.label} types`}>
      {chip}
      <div className="adv-metric-group__types">
        {types.map((t) => (
          <button
            key={t.key}
            type="button"
            className="adv-metric-type"
            aria-label={`${metric.label}: ${t.label}`}
            title={`${metric.label} — ${t.label}`}
            onClick={() => onSelect(metricKey, withMetricType(metric, t.key))}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const PlayerCard = ({ player, onSelect }) => {
  const avatarSrc = player.avatar_url || player.avatarUrl || player.profile_image || null;
  const jerseyNum = player.jerseyNumber ?? player.jersey_number;

  // Generate a deterministic hue from the player's name so each player has a
  // unique background colour even when no profile photo is available.
  const nameHash = (str = '') =>
    [...str].reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0);
  const hue = Math.abs(nameHash(playerDisplayName(player))) % 360;
  const initialsStyle = avatarSrc ? {} : {
    background: `linear-gradient(135deg, hsl(${hue},55%,28%), hsl(${(hue+40)%360},55%,22%))`,
    border: `2px solid hsl(${hue},55%,45%)`,
  };

  return (
    <button
      type="button"
      className="adv-player-card"
      onClick={() => onSelect(player)}
    >
      <div className="adv-player-card__avatar" style={initialsStyle}>
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={playerDisplayName(player)}
            className="adv-player-card__avatar-img"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <span
          className="adv-player-card__avatar-initials"
          style={{
            ...(avatarSrc ? { display: 'none' } : {}),
            color: `hsl(${hue},80%,80%)`,
            fontSize: '1.05rem',
          }}
        >
          {playerInitials(player)}
        </span>
      </div>
      <div className="adv-player-card__info">
        <span className="adv-player-card__name">{playerDisplayName(player)}</span>
        {jerseyNum != null && (
          <span className="adv-player-card__jersey-badge">#{jerseyNum}</span>
        )}
      </div>
      {player.position && (
        <span className="adv-player-card__pos">{player.position}</span>
      )}
    </button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const AdvancedScoringModal = ({
  isOpen,
  onClose,
  onConfirm,
  onDecrease,
  players = [],
  preSelectedPlayer = null,
  teamLabel = 'Team',
  scorer = null,
  participant,
  sportKey = '',
}) => {
  // Start at step 2 when a player is already selected inline on the card
  const [step, setStep]                           = useState(preSelectedPlayer ? 2 : 1);
  const [selectedPlayer, setSelectedPlayer]       = useState(preSelectedPlayer || null);
  const [selectedMetricKey, setSelectedMetricKey] = useState(null);
  const [selectedMetric, setSelectedMetric]       = useState(null);

  // Reset every time the modal opens or the pre-selected player changes
  useEffect(() => {
    if (isOpen) {
      if (preSelectedPlayer) {
        setStep(2);
        setSelectedPlayer(preSelectedPlayer);
      } else {
        setStep(1);
        setSelectedPlayer(null);
      }
      setSelectedMetricKey(null);
      setSelectedMetric(null);
    }
  }, [isOpen, preSelectedPlayer]);

  const quickMetrics = scorer?.getQuickMetrics ? scorer.getQuickMetrics(null) : [];
  const hasMetrics   = quickMetrics.length > 0;

  // Step 1 → 2: player chosen
  const handlePlayerSelect = useCallback((player) => {
    setSelectedPlayer(player);
    setSelectedMetricKey(null);
    setSelectedMetric(null);
    setStep(2);
  }, []);

  // Step 2 → confirm (or step 3 for block assist): action chosen
  const handleActionSelect = useCallback((metricKey, metric) => {
    setSelectedMetricKey(metricKey);
    setSelectedMetric(metric);

    if (isBlockAssist(metricKey)) {
      // selectedPlayer is the assisting player; advance to pick main blocker
      setStep(3);
    } else {
      onConfirm(selectedPlayer || preSelectedPlayer, metricKey, metric);
    }
  }, [selectedPlayer, preSelectedPlayer, onConfirm]);

  // Step 3: block partner tapped (shared block). Both players are credited a
  // Block Assist; the team wins one point, carried by the partner's write (AS-B08).
  const handleMainBlockerSelect = useCallback((player) => {
    onConfirm(player, 'blockAssist', SHARED_BLOCK_METRIC, [selectedPlayer]);
  }, [selectedPlayer, onConfirm]);

  // Quick –1 deduct (step 1 footer) — closes modal after deducting
  const handleDeduct = useCallback(() => {
    onDecrease?.();
    onClose();
  }, [onDecrease, onClose]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  if (!isOpen) return null;

  // Metrics for step 2 filtered to what the selected player can do
  const effectivePlayer = selectedPlayer || preSelectedPlayer;
  const metricsForPlayer = effectivePlayer
    ? quickMetrics.filter(({ key }) =>
        (scorer?.canRecordMetric(key, effectivePlayer) ?? { allowed: true }).allowed
      )
    : quickMetrics;

  // Eligible players for step 3 (blockSolo eligible, excluding the assisting player)
  const eligibleForStep3 = players.filter((p) => {
    if ((scorer?.canRecordMetric('blockSolo', p) ?? { allowed: true }).allowed === false) return false;
    const pid      = p.id          ?? p.userId;
    const assistId = selectedPlayer?.id ?? selectedPlayer?.userId;
    return pid !== assistId;
  });

  // ── Step 1: Player selector ─────────────────────────────────────────────────
  const renderStep1 = () => (
    <div className="adv-modal__step adv-modal__step-1">
      <div className="adv-modal__step-header">
        <h4>Who scored?</h4>
        <p className="adv-modal__step-sub">{teamLabel} — select the player</p>
      </div>

      {players.length === 0 ? (
        <div className="adv-modal__empty">
          <p>No players found for this team.</p>
          <button
            className="adv-modal__btn adv-modal__btn--next"
            onClick={() => handlePlayerSelect(null)}
          >
            Continue without player
          </button>
        </div>
      ) : (
        <div className="adv-player-grid">
          {players.map((p) => (
            <PlayerCard
              key={p.id ?? p.userId ?? playerDisplayName(p)}
              player={p}
              onSelect={handlePlayerSelect}
            />
          ))}
        </div>
      )}

      <div className="adv-modal__footer adv-modal__footer--step1">
        <button className="adv-modal__btn adv-modal__btn--cancel" onClick={onClose}>
          Cancel
        </button>
        {onDecrease && (
          <button
            className="adv-modal__btn adv-modal__btn--deduct"
            onClick={handleDeduct}
            title="Quick –1 score deduction"
            aria-label="Deduct one point"
          >
            −1
          </button>
        )}
      </div>
    </div>
  );

  // ── Step 2: Action selector ─────────────────────────────────────────────────
  const renderStep2 = () => (
    <div className="adv-modal__step adv-modal__step-2">
      <div className="adv-modal__step-header">
        <h4>What happened?</h4>
        <p className="adv-modal__step-sub">
          {playerDisplayName(selectedPlayer || preSelectedPlayer)} — select the action
        </p>
      </div>

      {!hasMetrics ? (
        <div className="adv-modal__empty">
          <p>No metrics configured for this sport.</p>
          <button
            className="adv-modal__btn adv-modal__btn--next"
            onClick={() =>
              handleActionSelect('score', { label: 'Score', affectsScore: true, isPositive: true })
            }
          >
            Record Score
          </button>
        </div>
      ) : metricsForPlayer.length === 0 ? (
        <div className="adv-modal__empty">
          No actions available for this player's position.
        </div>
      ) : (
        <div className="adv-quick-grid">
          {metricsForPlayer.map(({ key, metric }) => (
            <ActionChipGroup
              key={key}
              metricKey={key}
              metric={metric}
              player={effectivePlayer}
              selected={selectedMetricKey === key}
              onSelect={handleActionSelect}
            />
          ))}
        </div>
      )}

      <div className="adv-modal__footer">
        {!preSelectedPlayer && (
          <button className="adv-modal__btn adv-modal__btn--back" onClick={() => setStep(1)}>
            ← Back
          </button>
        )}
        <button className="adv-modal__btn adv-modal__btn--cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );

  // ── Step 3: Main blocker selector (block-assist flow only) ──────────────────
  const renderStep3 = () => (
    <div className="adv-modal__step adv-modal__step-3">
      <div className="adv-modal__step-header">
        <h4>Block (Solo) — Main Blocker</h4>
        <p className="adv-modal__step-sub">
          {teamLabel} — who made the primary block? (+1)
          {selectedPlayer && (
            <> — <em>{playerDisplayName(selectedPlayer)}</em> credited as assister</>
          )}
        </p>
      </div>

      {eligibleForStep3.length === 0 ? (
        <div className="adv-modal__empty">
          No eligible players (all non-Libero players already selected).
        </div>
      ) : (
        <div className="adv-player-grid">
          {eligibleForStep3.map((p) => (
            <PlayerCard
              key={p.id ?? p.userId ?? playerDisplayName(p)}
              player={p}
              onSelect={handleMainBlockerSelect}
            />
          ))}
        </div>
      )}

      <div className="adv-modal__footer">
        <button className="adv-modal__btn adv-modal__btn--back" onClick={() => setStep(2)}>
          ← Back
        </button>
        <button className="adv-modal__btn adv-modal__btn--cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );

  const totalSteps = isBlockAssist(selectedMetricKey) ? 3 : 2;

  const content = (
    <div
      className="adv-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <div className="adv-modal-card">
        <div className="adv-modal__header">
          <div className="adv-modal__title">Advanced Scoring</div>
          <button className="adv-modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="adv-modal__steps-indicator">
          {Array.from({ length: totalSteps }, (_, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="adv-modal__step-line" />}
              <span className={`adv-modal__step-dot ${step > i ? 'active' : ''}`}>{i + 1}</span>
            </React.Fragment>
          ))}
        </div>

        <div className="adv-modal__body">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default AdvancedScoringModal;

